import { CSSProperties } from 'react';

/**
 * StarRating — 5 stars. Interactive when `onChange` is given (tap a star to
 * set it; tapping the current value again keeps it, or with `clearable`
 * clears it by reporting 0), switched off with `disabled`, otherwise a small read-only display.
 */
export interface StarRatingProps {
  /** Current rating, 0 = none yet. */
  value: number;
  onChange?: (rating: number) => void;
  /** Tapping the star that is already set reports 0, so the rating can be taken back. */
  clearable?: boolean;
  /** Dimmed and not tappable (host / admin preview). */
  disabled?: boolean;
  /** Star size in px. Default 28 (interactive) / 12 (read-only). */
  size?: number;
  style?: CSSProperties;
}

export function StarRating({ value, onChange, clearable = false, disabled = false, size, style }: StarRatingProps) {
  const interactive = !!onChange && !disabled;
  const px = size ?? (onChange || disabled ? 28 : 12);
  return (
    <div
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={value > 0 ? `${value} out of 5 stars` : 'No rating yet'}
      style={{ display: 'inline-flex', gap: interactive || disabled ? 6 : 2, opacity: disabled ? 0.4 : 1, ...style }}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= value;
        const star = (
          <i
            className={`ti ti-star${filled ? '-filled' : ''}`}
            style={{ fontSize: px, lineHeight: 1, color: filled ? 'var(--accent)' : 'var(--text-dim)' }}
          />
        );
        if (!onChange) return <span key={n}>{star}</span>;
        return (
          <button className="sk-hit"
            key={n}
            type="button"
            role="radio"
            aria-checked={n === value}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            disabled={disabled}
            onClick={() => onChange(clearable && n === value ? 0 : n)}
            style={{ background: 'none', border: 'none', padding: 2, cursor: disabled ? 'default' : 'pointer' }}
          >
            {star}
          </button>
        );
      })}
    </div>
  );
}

export default StarRating;
